import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * AuthCallback — landing route for the Emergent Google Auth flow.
 *
 * The OAuth provider redirects here with the session id in the URL
 * fragment (`/auth/callback#session_id=...`). We extract it, exchange
 * via the backend, then bounce back to whichever page the user came
 * from (stashed in `sessionStorage["tt:auth:return-to"]` by the modal).
 *
 * Errors are surfaced via toast and the user is sent home rather than
 * stuck on a blank screen.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [msg, setMsg] = useState("Signing you in…");

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const frag = window.location.hash || "";
      const params = new URLSearchParams(frag.startsWith("#") ? frag.slice(1) : frag);
      const sessionId = params.get("session_id");
      if (!sessionId) {
        if (cancelled) return;
        toast.error("Sign-in didn't return a session. Please try again.");
        navigate("/", { replace: true });
        return;
      }
      const res = await loginWithGoogle(sessionId);
      if (cancelled) return;
      if (!res.ok) {
        toast.error(res.error || "Couldn't finish signing in.");
        navigate("/", { replace: true });
        return;
      }
      toast.success("Signed in.");
      let dest = "/";
      try {
        dest = sessionStorage.getItem("tt:auth:return-to") || "/";
        sessionStorage.removeItem("tt:auth:return-to");
        sessionStorage.removeItem("tt:auth:gated");
      } catch {}
      navigate(dest, { replace: true });
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="auth-callback">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full border-2 border-muted border-t-primary h-7 w-7" />
        <p className="mt-3 text-sm text-muted-foreground">{msg}</p>
      </div>
    </div>
  );
}
